// import { PaymentStatus } from '@repo/common';
// import { orderReceipt } from './order-print';
import { useAppSelector } from '@features';
import { InvoiceGenerator } from '@repo/pdf';
import {
    Button,
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
} from '@repo/shadcn';
import { format } from 'date-fns';
import { Calendar, Check, ChevronDown, Package } from 'lucide-react';
import { useState } from 'react';

import { useGetSingleOrderQuery } from '@/features/slice/order';
import { useGetStoreSettingsQuery } from '@/features/slice/settings';
import { formatCurrencyInBDT } from '@/utils';
import { mapOrderToInvoice } from '@/utils/mappers';

export const OrderInvoice = () => {
    const { modalData } = useAppSelector((state) => state.eventSlice);
    const [isOpen, setIsOpen] = useState(false);

    const {
        data: orderData,
        isLoading: isLoadingOrder,
        isSuccess: isSuccessOrder,
    } = useGetSingleOrderQuery(modalData.original.id);

    const {
        data: storeData,
        isLoading: isLoadingStore,
        isSuccess: isSuccessStore,
    } = useGetStoreSettingsQuery({});

    const isLoading = isLoadingOrder || isLoadingStore;
    const isSuccess = isSuccessOrder && isSuccessStore;

    if (isLoading) {
        return 'Loading...';
    }
    if (!isLoading && isSuccess && orderData.data) {
        const totalQuantity = orderData?.data.items.reduce(
            (sum, item) => sum + item.quantity,
            0,
        );
        const totalAmount = orderData?.data.items.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0,
        );

        const submit = () => {
            if (orderData?.data.id) {
                const invoice = new InvoiceGenerator(
                    mapOrderToInvoice(orderData.data, storeData?.data),
                );
                // invoice.generate();
                invoice.print();
                // orderReceipt(
                //   {
                //     invoice: data?.data.invoice_number,
                //     date: new Date().toLocaleString(),
                //     items: data?.data.items,
                //     subtotal: totalAmount,
                //     tax: 0,
                //     total: totalAmount,
                //     paymentMethod: 'Hand Cash',
                //     paymentStatus: data.data.payment?.status || PaymentStatus.DUE,
                //   },
                //   'print',
                // );
            }
        };

        return (
            <Card className="mx-auto w-full max-w-4xl">
                <CardHeader className="border-b">
                    <CardTitle className="flex items-center gap-2 text-xl">
                        <Package className="h-5 w-5" />
                        Invoice Summary
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[50%]">Item</TableHead>
                                <TableHead className="text-right">
                                    Unit Price
                                </TableHead>
                                <TableHead className="text-right">
                                    Quantity
                                </TableHead>
                                <TableHead className="text-right">
                                    Total
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {orderData.data.items.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell className="font-medium">
                                        <div>
                                            {item.product_name}
                                            <div className="mt-1 text-xs text-muted-foreground">
                                                <Collapsible
                                                    open={isOpen}
                                                    onOpenChange={setIsOpen}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="h-3 w-3" />

                                                        <CollapsibleTrigger
                                                            asChild
                                                        >
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-6 w-6 p-0"
                                                            >
                                                                <ChevronDown className="h-3 w-3" />
                                                                <span className="sr-only">
                                                                    Toggle
                                                                    details
                                                                </span>
                                                            </Button>
                                                        </CollapsibleTrigger>
                                                    </div>
                                                    <CollapsibleContent>
                                                        <div className="mt-2 space-y-1">
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <span className="text-muted-foreground">
                                                                    ID:
                                                                </span>{' '}
                                                                {item.id}
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <span className="text-muted-foreground">
                                                                    Order ID:
                                                                </span>{' '}
                                                                {item.order_id}
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <span className="text-muted-foreground">
                                                                    Created:
                                                                </span>{' '}
                                                                {format(
                                                                    new Date(
                                                                        item.created_at,
                                                                    ),
                                                                    'MMM d, yyyy HH:mm',
                                                                )}
                                                            </div>
                                                        </div>
                                                    </CollapsibleContent>
                                                </Collapsible>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {formatCurrencyInBDT(
                                            Number(item.price.toFixed(2)),
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {item.quantity}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {formatCurrencyInBDT(
                                            Number(
                                                (
                                                    item.price * item.quantity
                                                ).toFixed(2),
                                            ),
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={2}>Total</TableCell>
                                <TableCell className="text-right">
                                    {totalQuantity}
                                </TableCell>
                                <TableCell className="text-right">
                                    {formatCurrencyInBDT(
                                        Number(totalAmount.toFixed(2)),
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </CardContent>
                <CardFooter className="flex justify-between border-t p-4">
                    <div className="text-sm text-muted-foreground">
                        {orderData.data.items.length}{' '}
                        {orderData.data.items.length === 1 ? 'item' : 'items'}{' '}
                        in this invoice
                    </div>
                    <Button onClick={submit} size="sm" className="gap-1">
                        <Check className="h-4 w-4" /> Print Invoice
                    </Button>
                </CardFooter>
            </Card>
        );
    }
};
